import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Bot, CalendarDays, CheckCircle2, ChevronDown, ChevronRight, Circle,
  ClipboardCheck, Clock3, ListChecks, Plus, RotateCcw, Save, Trash2, UserPlus, X
} from 'lucide-react';
import {
  MASTER_MEETING_ID,
  MASTER_MEETING_TEMPLATE,
  MEETING_TEMPLATES,
  buildRecurringMeetingGroups,
  countTemplateItems,
  getChecklistKey
} from './meetingTemplates.mjs';
import {
  formatSundaySchedule,
  isFirstSundayOfMonth,
  nextSundayAfter,
  scheduleStatus,
  scheduledSundayForNow
} from './lib/meetingSchedule.mjs';

const STORAGE_KEY = 'vasia-dashboard-meetings-v1';

const DEFAULT_BOTS = [
  {
    id: 'performance-bot',
    name: 'Performance Review Bot',
    role: 'Performance',
    description: 'Reviews VIP revenue, subscribers, retention and key business movements.',
    active: true
  },
  {
    id: 'chatting-bot',
    name: 'Chatting QA Bot',
    role: 'Chatting',
    description: 'Reviews chatter speed, quality, sales behaviour and whale follow-ups.',
    active: true
  },
  {
    id: 'social-bot',
    name: 'Social Media Bot',
    role: 'Marketing',
    description: 'Reviews Instagram, Reddit, X, streaming and media performance.',
    active: true
  },
  {
    id: 'tracking-bot',
    name: 'Tracking & Attribution Bot',
    role: 'Analytics',
    description: 'Checks tracking links, source attribution, conversion and provider ROI.',
    active: true
  },
  {
    id: 'reel-bot',
    name: 'Reel & Automation Bot',
    role: 'Content',
    description: 'Reviews Reel performance, filming workflow, AI Reel Creator and repurposing opportunities.',
    active: true
  }
];

const EMPTY_STATE = {
  sessions: [],
  followUps: [],
  bots: DEFAULT_BOTS
};

const loadState = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    return {
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      followUps: Array.isArray(parsed.followUps) ? parsed.followUps : [],
      bots: Array.isArray(parsed.bots) && parsed.bots.length ? parsed.bots : DEFAULT_BOTS
    };
  } catch {
    return EMPTY_STATE;
  }
};

const makeId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const todayValue = () => new Date().toISOString().slice(0, 10);

const formatDate = (value) => {
  if (!value) return 'No date';
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const progressFor = (session) => {
  const template = MEETING_TEMPLATES.find((item) => item.id === session.templateId);
  const groups = session.agendaGroups || template?.groups || [];
  const total = countTemplateItems({ groups });
  const done = total ? Object.values(session.checked || {}).filter(Boolean).length : 0;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
};

const cleanAgendaLines = (value = '') => value
  .split(/\r?\n/)
  .map((line) => line.replace(/^[-*•\d.)\s]+/, '').trim())
  .filter(Boolean);

const carryForwardItems = (session, followUps) => {
  const drafted = cleanAgendaLines(session?.nextMeetingDraft || '');
  const actions = followUps
    .filter((item) => item.meetingId === session?.id && !item.done)
    .map((item) => `Follow up: ${item.task}${item.owner ? ` (Owner: ${item.owner})` : ''}`);
  const fallback = session?.notes?.trim() && drafted.length === 0
    ? ['Review the previous meeting notes and confirm every unresolved decision or data gap.']
    : [];
  return Array.from(new Set([...drafted, ...actions, ...fallback])).slice(0, 12);
};

const makeScheduledMeeting = ({ date, previousSession = null, followUps = [] }) => {
  const carryForward = previousSession ? carryForwardItems(previousSession, followUps) : [];
  const agendaGroups = previousSession
    ? buildRecurringMeetingGroups({ carryForward, includeMonthly: isFirstSundayOfMonth(date) })
    : MASTER_MEETING_TEMPLATE.groups.map((group) => ({ ...group, items: [...group.items] }));
  return {
    id: makeId('meeting'),
    templateId: MASTER_MEETING_ID,
    title: previousSession ? 'Vasia Sunday Review' : MASTER_MEETING_TEMPLATE.title,
    purpose: previousSession
      ? 'A focused weekly review prepared from the previous outcome, unfinished actions and the topics drafted for this Sunday.'
      : MASTER_MEETING_TEMPLATE.purpose,
    date,
    status: 'Scheduled',
    checked: {},
    notes: '',
    nextMeetingDraft: '',
    agendaGroups,
    carryForwardNotes: previousSession?.notes?.trim() || '',
    previousMeetingId: previousSession?.id || '',
    botIds: [],
    createdAt: new Date().toISOString(),
    completedAt: null
  };
};

function MeetingWorkspace({ onClose }) {
  const [data, setData] = useState(loadState);
  const [view, setView] = useState('home');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [newBot, setNewBot] = useState({ name: '', role: '', description: '' });
  const [newFollowUp, setNewFollowUp] = useState({ task: '', owner: '', dueDate: '', priority: 'Medium', meetingId: '' });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    setData((current) => {
      const activeSundayMeeting = current.sessions.find((session) => (
        session.templateId === MASTER_MEETING_ID && session.status !== 'Complete'
      ));
      if (activeSundayMeeting) return current;
      const latestCompleted = current.sessions.find((session) => (
        session.templateId === MASTER_MEETING_ID && session.status === 'Complete'
      ));
      const date = latestCompleted ? nextSundayAfter(latestCompleted.date) : scheduledSundayForNow();
      return {
        ...current,
        sessions: [makeScheduledMeeting({ date, previousSession: latestCompleted, followUps: current.followUps }), ...current.sessions]
      };
    });
  }, []);

  const activeSession = useMemo(
    () => data.sessions.find((session) => session.id === activeSessionId) || null,
    [activeSessionId, data.sessions]
  );

  const activeTemplate = useMemo(
    () => {
      if (!activeSession) return null;
      const template = MEETING_TEMPLATES.find((item) => item.id === activeSession.templateId);
      if (activeSession.agendaGroups) {
        return {
          ...(template || MASTER_MEETING_TEMPLATE),
          title: activeSession.title,
          purpose: activeSession.purpose || template?.purpose || MASTER_MEETING_TEMPLATE.purpose,
          groups: activeSession.agendaGroups
        };
      }
      return template || { ...MASTER_MEETING_TEMPLATE, title: activeSession.title, groups: [] };
    },
    [activeSession]
  );

  const openSession = (sessionId) => {
    setData((current) => ({
      ...current,
      sessions: current.sessions.map((session) => (
        session.id === sessionId && session.status === 'Scheduled'
          ? { ...session, status: 'In progress' }
          : session
      ))
    }));
    setActiveSessionId(sessionId);
    setView('session');
  };

  const startMeeting = () => {
    const session = makeScheduledMeeting({ date: scheduledSundayForNow() });
    session.status = 'In progress';
    setData((current) => ({ ...current, sessions: [session, ...current.sessions] }));
    setActiveSessionId(session.id);
    setView('session');
  };

  const updateSession = (patch) => {
    if (!activeSessionId) return;
    setData((current) => ({
      ...current,
      sessions: current.sessions.map((session) => (
        session.id === activeSessionId ? { ...session, ...patch } : session
      ))
    }));
  };

  const toggleChecklist = (key) => {
    if (!activeSession) return;
    const checked = { ...(activeSession.checked || {}), [key]: !activeSession.checked?.[key] };
    updateSession({
      checked,
      status: 'In progress',
      completedAt: null
    });
  };

  const resetChecklist = () => {
    updateSession({ checked: {}, status: 'In progress', completedAt: null });
  };

  const deleteSession = () => {
    if (!activeSessionId) return;
    setData((current) => ({
      ...current,
      sessions: current.sessions.filter((session) => session.id !== activeSessionId),
      followUps: current.followUps.map((item) => item.meetingId === activeSessionId ? { ...item, meetingId: '' } : item)
    }));
    setActiveSessionId(null);
    setView('home');
  };

  const finishMeetingAndDraftNext = () => {
    if (!activeSessionId) return;
    setData((current) => {
      const session = current.sessions.find((item) => item.id === activeSessionId);
      if (!session) return current;
      const completed = { ...session, status: 'Complete', completedAt: new Date().toISOString() };
      const nextDate = nextSundayAfter(session.date || todayValue());
      const alreadyScheduled = current.sessions.some((item) => (
        item.templateId === MASTER_MEETING_ID && item.status !== 'Complete' && item.id !== session.id
      ));
      const sessions = current.sessions.map((item) => item.id === session.id ? completed : item);
      if (!alreadyScheduled) {
        sessions.unshift(makeScheduledMeeting({ date: nextDate, previousSession: completed, followUps: current.followUps }));
      }
      return { ...current, sessions };
    });
    setActiveSessionId(null);
    setView('home');
  };

  const addFollowUp = (defaults = {}) => {
    const task = (defaults.task ?? newFollowUp.task).trim();
    if (!task) return;
    const item = {
      id: makeId('followup'),
      task,
      owner: defaults.owner ?? newFollowUp.owner,
      dueDate: defaults.dueDate ?? newFollowUp.dueDate,
      priority: defaults.priority ?? newFollowUp.priority,
      meetingId: defaults.meetingId ?? newFollowUp.meetingId ?? '',
      done: false,
      createdAt: new Date().toISOString()
    };
    setData((current) => ({ ...current, followUps: [item, ...current.followUps] }));
    setNewFollowUp({ task: '', owner: '', dueDate: '', priority: 'Medium', meetingId: '' });
  };

  const updateFollowUp = (id, patch) => {
    setData((current) => ({
      ...current,
      followUps: current.followUps.map((item) => item.id === id ? { ...item, ...patch } : item)
    }));
  };

  const deleteFollowUp = (id) => {
    setData((current) => ({ ...current, followUps: current.followUps.filter((item) => item.id !== id) }));
  };

  const addBot = () => {
    if (!newBot.name.trim()) return;
    const bot = {
      id: makeId('bot'),
      name: newBot.name.trim(),
      role: newBot.role.trim() || 'Meeting support',
      description: newBot.description.trim(),
      active: true
    };
    setData((current) => ({ ...current, bots: [...current.bots, bot] }));
    setNewBot({ name: '', role: '', description: '' });
  };

  const updateBot = (id, patch) => {
    setData((current) => ({
      ...current,
      bots: current.bots.map((bot) => bot.id === id ? { ...bot, ...patch } : bot)
    }));
  };

  const deleteBot = (id) => {
    setData((current) => ({
      ...current,
      bots: current.bots.filter((bot) => bot.id !== id),
      sessions: current.sessions.map((session) => ({
        ...session,
        botIds: (session.botIds || []).filter((botId) => botId !== id)
      }))
    }));
  };

  const toggleBotForSession = (botId) => {
    if (!activeSession) return;
    const ids = activeSession.botIds || [];
    const botIds = ids.includes(botId) ? ids.filter((id) => id !== botId) : [...ids, botId];
    updateSession({ botIds });
  };

  const outstandingFollowUps = data.followUps.filter((item) => !item.done).length;
  const completedMeetings = data.sessions.filter((session) => session.status === 'Complete').length;
  const upcomingMeeting = data.sessions.find((session) => (
    session.templateId === MASTER_MEETING_ID && session.status !== 'Complete'
  ));

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-[#050713]/95 text-white backdrop-blur-xl">
      <div className="mx-auto min-h-screen max-w-[1680px] p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-purple-300/15 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-pink-300">
              <ClipboardCheck size={15} /> Meetings command centre
            </div>
            <h1 className="text-2xl font-bold">Meetings & Follow-ups</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-400">
              One Vasia review every Sunday at 11:00 Norway time. Notes, unfinished actions and the next-agenda draft carry into the following meeting.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-700 bg-gray-900 px-4 py-2 text-sm font-medium text-gray-200 transition hover:border-pink-400/40 hover:text-white"
          >
            <X size={17} /> Back to dashboard
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <button type="button" onClick={() => setView('home')} className={`rounded-2xl border p-4 text-left transition ${view === 'home' ? 'border-pink-400/50 bg-pink-400/10' : 'border-gray-800 bg-gray-950/70 hover:border-gray-700'}`}>
            <ListChecks size={18} className="mb-2 text-pink-300" />
            <div className="font-semibold">Sunday review</div>
            <div className="mt-1 text-xs text-gray-400">One recurring meeting · 11:00 Norway</div>
          </button>
          <button type="button" onClick={() => setView('history')} className={`rounded-2xl border p-4 text-left transition ${view === 'history' ? 'border-pink-400/50 bg-pink-400/10' : 'border-gray-800 bg-gray-950/70 hover:border-gray-700'}`}>
            <CalendarDays size={18} className="mb-2 text-violet-300" />
            <div className="font-semibold">Meeting history</div>
            <div className="mt-1 text-xs text-gray-400">{data.sessions.length} meetings · {completedMeetings} complete</div>
          </button>
          <button type="button" onClick={() => setView('followups')} className={`rounded-2xl border p-4 text-left transition ${view === 'followups' ? 'border-pink-400/50 bg-pink-400/10' : 'border-gray-800 bg-gray-950/70 hover:border-gray-700'}`}>
            <CheckCircle2 size={18} className="mb-2 text-cyan-300" />
            <div className="font-semibold">Follow-ups</div>
            <div className="mt-1 text-xs text-gray-400">{outstandingFollowUps} outstanding actions</div>
          </button>
          <button type="button" onClick={() => setView('bots')} className={`rounded-2xl border p-4 text-left transition ${view === 'bots' ? 'border-pink-400/50 bg-pink-400/10' : 'border-gray-800 bg-gray-950/70 hover:border-gray-700'}`}>
            <Bot size={18} className="mb-2 text-emerald-300" />
            <div className="font-semibold">Meeting bots</div>
            <div className="mt-1 text-xs text-gray-400">{data.bots.filter((bot) => bot.active).length} active assistants</div>
          </button>
        </div>

        {view === 'home' && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.6fr)]">
            <section className="overflow-hidden rounded-3xl border border-pink-400/25 bg-gradient-to-br from-pink-400/10 via-violet-400/5 to-gray-950/80 p-6 sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-pink-300">One meeting · every Sunday</div>
                  <h2 className="mt-2 text-2xl font-bold">{upcomingMeeting?.title || MASTER_MEETING_TEMPLATE.title}</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-300">{upcomingMeeting?.purpose || MASTER_MEETING_TEMPLATE.purpose}</p>
                </div>
                {upcomingMeeting && (
                  <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-200">
                    {scheduleStatus(upcomingMeeting.date)}
                  </span>
                )}
              </div>

              {upcomingMeeting ? (() => {
                const progress = progressFor(upcomingMeeting);
                return (
                  <div className="mt-6">
                    <div className="rounded-2xl border border-gray-700/70 bg-gray-950/55 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-gray-500">Next scheduled review</div>
                          <div className="mt-1 text-lg font-semibold text-white">{formatSundaySchedule(upcomingMeeting.date)}</div>
                        </div>
                        <span className="rounded-full border border-gray-700 px-3 py-1 text-xs text-gray-300">{progress.total} checks</span>
                      </div>
                      <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-800"><div className="h-full rounded-full bg-pink-400" style={{ width: `${progress.pct}%` }} /></div>
                      <div className="mt-2 text-xs text-gray-500">{progress.done}/{progress.total} reviewed · {upcomingMeeting.status}</div>
                    </div>
                    <button type="button" onClick={() => openSession(upcomingMeeting.id)} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-pink-400">
                      {upcomingMeeting.status === 'Scheduled' ? 'Open Sunday meeting' : 'Continue Sunday meeting'} <ChevronRight size={17} />
                    </button>
                  </div>
                );
              })() : (
                <button type="button" onClick={startMeeting} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-pink-400"><Plus size={16} /> Prepare Sunday meeting</button>
              )}
            </section>

            <aside className="rounded-3xl border border-gray-800 bg-gray-950/75 p-6">
              <h3 className="font-semibold text-white">How the weekly cycle works</h3>
              <ol className="mt-4 space-y-4 text-sm leading-6 text-gray-300">
                <li><strong className="text-pink-300">1.</strong> Hold one meeting at 11:00 Norway time every Sunday.</li>
                <li><strong className="text-pink-300">2.</strong> Record decisions, outcomes and no more than three priority actions.</li>
                <li><strong className="text-pink-300">3.</strong> Write any topics that must be raised at the next meeting.</li>
                <li><strong className="text-pink-300">4.</strong> Finish the meeting to create the following Sunday’s draft automatically.</li>
              </ol>
              <p className="mt-5 rounded-xl border border-violet-400/20 bg-violet-400/5 p-4 text-xs leading-5 text-violet-100">The first Sunday meeting contains the complete master review. Later Sundays use the short weekly checklist, carry-forward notes and unfinished actions. The first Sunday of each month also adds the monthly strategic layer.</p>
            </aside>
          </div>
        )}

        {view === 'history' && (
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-semibold">Meeting history</h2>
              <p className="mt-1 text-sm text-gray-400">Reopen any previous meeting to see its checklist, notes and assigned bots.</p>
            </div>
            <div className="space-y-3">
              {data.sessions.length === 0 && <div className="rounded-2xl border border-gray-800 bg-gray-950/70 p-6 text-sm text-gray-400">No meetings have been started yet.</div>}
              {data.sessions.map((session) => {
                const progress = progressFor(session);
                return (
                  <button key={session.id} type="button" onClick={() => openSession(session.id)} className="flex w-full flex-col gap-3 rounded-2xl border border-gray-800 bg-gray-950/70 p-4 text-left transition hover:border-pink-400/30 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="font-semibold">{session.title}</div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>{formatDate(session.date)}</span>
                        <span>{progress.done}/{progress.total} checks</span>
                        <span>{session.botIds?.length || 0} bots</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs ${session.status === 'Complete' ? 'bg-emerald-400/10 text-emerald-300' : 'bg-amber-400/10 text-amber-300'}`}>{session.status}</span>
                      <ChevronRight size={17} className="text-gray-500" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {view === 'followups' && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <section>
              <div className="mb-4">
                <h2 className="text-lg font-semibold">Follow-up queue</h2>
                <p className="mt-1 text-sm text-gray-400">Actions stay here until they are ticked complete.</p>
              </div>
              <div className="space-y-3">
                {data.followUps.length === 0 && <div className="rounded-2xl border border-gray-800 bg-gray-950/70 p-6 text-sm text-gray-400">No follow-ups yet. Add them here or directly from a meeting.</div>}
                {data.followUps.map((item) => {
                  const linkedMeeting = data.sessions.find((session) => session.id === item.meetingId);
                  return (
                    <div key={item.id} className={`rounded-2xl border p-4 ${item.done ? 'border-gray-800 bg-gray-950/40 opacity-60' : 'border-gray-800 bg-gray-950/75'}`}>
                      <div className="flex items-start gap-3">
                        <button type="button" onClick={() => updateFollowUp(item.id, { done: !item.done })} className="mt-0.5 text-pink-300" aria-label={item.done ? 'Mark follow-up incomplete' : 'Mark follow-up complete'}>
                          {item.done ? <CheckCircle2 size={21} /> : <Circle size={21} />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className={item.done ? 'line-through' : ''}>{item.task}</div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-400">
                            {item.owner && <span className="rounded-full bg-gray-900 px-2 py-1">Owner: {item.owner}</span>}
                            {item.dueDate && <span className="rounded-full bg-gray-900 px-2 py-1">Due: {formatDate(item.dueDate)}</span>}
                            <span className="rounded-full bg-gray-900 px-2 py-1">{item.priority}</span>
                            {linkedMeeting && <span className="rounded-full bg-gray-900 px-2 py-1">From: {linkedMeeting.title}</span>}
                          </div>
                        </div>
                        <button type="button" onClick={() => deleteFollowUp(item.id)} className="text-gray-500 transition hover:text-red-300" aria-label="Delete follow-up"><Trash2 size={17} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <aside className="h-fit rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
              <h3 className="font-semibold">Add follow-up</h3>
              <div className="mt-4 space-y-3">
                <label className="block text-sm text-gray-300">Action
                  <textarea value={newFollowUp.task} onChange={(event) => setNewFollowUp((current) => ({ ...current, task: event.target.value }))} rows={3} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white outline-none focus:border-pink-400" placeholder="What needs to happen?" />
                </label>
                <label className="block text-sm text-gray-300">Owner
                  <input value={newFollowUp.owner} onChange={(event) => setNewFollowUp((current) => ({ ...current, owner: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="Adam, Vasia, agency..." />
                </label>
                <label className="block text-sm text-gray-300">Due date
                  <input type="date" value={newFollowUp.dueDate} onChange={(event) => setNewFollowUp((current) => ({ ...current, dueDate: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" />
                </label>
                <label className="block text-sm text-gray-300">Priority
                  <select value={newFollowUp.priority} onChange={(event) => setNewFollowUp((current) => ({ ...current, priority: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400">
                    <option>Low</option><option>Medium</option><option>High</option><option>Urgent</option>
                  </select>
                </label>
                <label className="block text-sm text-gray-300">Link to meeting
                  <select value={newFollowUp.meetingId} onChange={(event) => setNewFollowUp((current) => ({ ...current, meetingId: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400">
                    <option value="">No meeting</option>
                    {data.sessions.map((session) => <option key={session.id} value={session.id}>{session.title} · {formatDate(session.date)}</option>)}
                  </select>
                </label>
                <button type="button" onClick={() => addFollowUp()} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-pink-500 px-4 py-2.5 text-sm font-semibold hover:bg-pink-400"><Plus size={16} /> Add follow-up</button>
              </div>
            </aside>
          </div>
        )}

        {view === 'bots' && (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <section>
              <div className="mb-4">
                <h2 className="text-lg font-semibold">Meeting bots</h2>
                <p className="mt-1 text-sm text-gray-400">This is the roster of assistants you want involved in reviews. Assign the relevant bots inside each meeting.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {data.bots.map((bot) => (
                  <article key={bot.id} className="rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-violet-400/10 p-2 text-violet-300"><Bot size={19} /></div>
                        <div>
                          <h3 className="font-semibold">{bot.name}</h3>
                          <div className="mt-1 text-xs uppercase tracking-wider text-pink-300">{bot.role}</div>
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteBot(bot.id)} className="text-gray-600 transition hover:text-red-300" aria-label={`Delete ${bot.name}`}><Trash2 size={16} /></button>
                    </div>
                    <p className="mt-3 text-sm text-gray-400">{bot.description || 'No responsibility description yet.'}</p>
                    <label className="mt-4 flex items-center gap-2 text-sm text-gray-300">
                      <input type="checkbox" checked={bot.active} onChange={(event) => updateBot(bot.id, { active: event.target.checked })} className="h-4 w-4 accent-pink-500" /> Active
                    </label>
                  </article>
                ))}
              </div>
            </section>

            <aside className="h-fit rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
              <h3 className="flex items-center gap-2 font-semibold"><UserPlus size={17} /> Add meeting bot</h3>
              <div className="mt-4 space-y-3">
                <label className="block text-sm text-gray-300">Bot name
                  <input value={newBot.name} onChange={(event) => setNewBot((current) => ({ ...current, name: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="e.g. Instagram Review Bot" />
                </label>
                <label className="block text-sm text-gray-300">Role
                  <input value={newBot.role} onChange={(event) => setNewBot((current) => ({ ...current, role: event.target.value }))} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="e.g. Social performance" />
                </label>
                <label className="block text-sm text-gray-300">What should it cover?
                  <textarea value={newBot.description} onChange={(event) => setNewBot((current) => ({ ...current, description: event.target.value }))} rows={4} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="Describe what this bot should review or bring to meetings." />
                </label>
                <button type="button" onClick={addBot} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-500 px-4 py-2.5 text-sm font-semibold hover:bg-violet-400"><Plus size={16} /> Add bot</button>
              </div>
            </aside>
          </div>
        )}

        {view === 'session' && activeSession && activeTemplate && (
          <div>
            <button type="button" onClick={() => setView('home')} className="mb-4 inline-flex items-center gap-2 text-sm text-gray-400 transition hover:text-white"><ArrowLeft size={16} /> Back to Sunday review</button>

            <div className="mb-6 rounded-2xl border border-pink-400/20 bg-gradient-to-br from-pink-400/10 via-violet-400/5 to-transparent p-5 sm:p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-3xl">
                  <div className="text-xs uppercase tracking-[0.22em] text-pink-300">{activeTemplate.frequency}</div>
                  <h2 className="mt-2 text-2xl font-bold">{activeSession.title}</h2>
                  <p className="mt-2 text-sm text-gray-400">{activeTemplate.purpose}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={resetChecklist} className="inline-flex items-center gap-2 rounded-xl border border-gray-700 bg-gray-950/70 px-3 py-2 text-sm text-gray-300 hover:text-white"><RotateCcw size={15} /> Reset checks</button>
                  <button type="button" onClick={finishMeetingAndDraftNext} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-400"><CheckCircle2 size={15} /> Finish & draft next Sunday</button>
                  <button type="button" onClick={deleteSession} className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-400/5 px-3 py-2 text-sm text-red-300 hover:bg-red-400/10"><Trash2 size={15} /> Delete meeting</button>
                </div>
              </div>

              {(() => {
                const progress = progressFor(activeSession);
                return (
                  <div className="mt-5">
                    <div className="mb-2 flex items-center justify-between text-xs text-gray-400"><span>{progress.done} of {progress.total} checks complete</span><strong className="text-white">{progress.pct}%</strong></div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-900"><div className="h-full rounded-full bg-pink-400 transition-all" style={{ width: `${progress.pct}%` }} /></div>
                  </div>
                );
              })()}
            </div>

            {activeSession.carryForwardNotes && (
              <div className="mb-6 rounded-2xl border border-violet-400/25 bg-violet-400/8 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-300">Previous meeting outcome</div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-300">{activeSession.carryForwardNotes}</p>
              </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px]">
              <main className="space-y-4">
                {activeTemplate.groups.map((group, groupIndex) => (
                  <details key={group.title} open={groupIndex === 0} className="group rounded-2xl border border-gray-800 bg-gray-950/75 p-4 sm:p-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{group.title}</h3>
                        {group.purpose && <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-400">{group.purpose}</p>}
                        <div className="mt-1 text-xs text-gray-500">{group.items.filter((_, itemIndex) => activeSession.checked?.[getChecklistKey(groupIndex, itemIndex)]).length}/{group.items.length} complete</div>
                      </div>
                      <ChevronDown size={18} className="text-gray-500 transition group-open:rotate-180" />
                    </summary>
                    <div className="mt-4 space-y-2 border-t border-gray-800 pt-4">
                      {group.items.map((item, itemIndex) => {
                        const key = getChecklistKey(groupIndex, itemIndex);
                        const checked = Boolean(activeSession.checked?.[key]);
                        return (
                          <label key={key} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm transition ${checked ? 'border-emerald-400/20 bg-emerald-400/5 text-gray-400' : 'border-gray-800 bg-gray-900/50 text-gray-200 hover:border-gray-700'}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleChecklist(key)} className="mt-0.5 h-4 w-4 shrink-0 accent-pink-500" />
                            <span className={checked ? 'line-through' : ''}>{item}</span>
                          </label>
                        );
                      })}
                    </div>
                  </details>
                ))}
              </main>

              <aside className="space-y-4">
                <section className="rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
                  <h3 className="flex items-center gap-2 font-semibold"><CalendarDays size={17} /> Meeting details</h3>
                  <label className="mt-4 block text-sm text-gray-300">Meeting date
                    <input type="date" value={activeSession.date} onChange={(event) => updateSession({ date: event.target.value })} className="mt-1 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" />
                  </label>
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500"><Clock3 size={14} /> Status: <span className={activeSession.status === 'Complete' ? 'text-emerald-300' : 'text-amber-300'}>{activeSession.status}</span></div>
                </section>

                <section className="rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
                  <h3 className="flex items-center gap-2 font-semibold"><Bot size={17} /> Bots for this meeting</h3>
                  <p className="mt-1 text-xs text-gray-500">Tick the assistants that should prepare or review this meeting.</p>
                  <div className="mt-4 space-y-2">
                    {data.bots.filter((bot) => bot.active).map((bot) => (
                      <label key={bot.id} className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-800 bg-gray-900/50 p-3 text-sm">
                        <input type="checkbox" checked={(activeSession.botIds || []).includes(bot.id)} onChange={() => toggleBotForSession(bot.id)} className="mt-0.5 h-4 w-4 accent-violet-500" />
                        <span><strong className="block font-medium text-gray-200">{bot.name}</strong><span className="mt-0.5 block text-xs text-gray-500">{bot.role}</span></span>
                      </label>
                    ))}
                  </div>
                  <button type="button" onClick={() => setView('bots')} className="mt-3 text-xs text-pink-300 hover:text-pink-200">Manage meeting bots →</button>
                </section>

                <section className="rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
                  <h3 className="flex items-center gap-2 font-semibold"><Save size={17} /> Meeting notes</h3>
                  <textarea value={activeSession.notes || ''} onChange={(event) => updateSession({ notes: event.target.value })} rows={9} className="mt-3 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="Decisions, observations, numbers to revisit..." />
                  <label className="mt-4 block text-sm font-medium text-gray-200">Draft topics for next Sunday
                    <textarea value={activeSession.nextMeetingDraft || ''} onChange={(event) => updateSession({ nextMeetingDraft: event.target.value })} rows={6} className="mt-2 w-full rounded-xl border border-violet-400/25 bg-violet-950/20 px-3 py-2 text-sm text-white outline-none focus:border-violet-400" placeholder={'One topic per line, for example:\nReview the new Instagram hook test\nDecide whether to keep the Reddit provider'} />
                  </label>
                  <p className="mt-2 text-xs leading-5 text-gray-500">Everything saves automatically. When you finish, these topics, the notes and unfinished actions prepare the next Sunday agenda.</p>
                </section>

                <section className="rounded-2xl border border-gray-800 bg-gray-950/75 p-5">
                  <h3 className="flex items-center gap-2 font-semibold"><CheckCircle2 size={17} /> Priority actions</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500">Leave with no more than three actions for the next seven days.</p>
                  <MeetingFollowUpQuickAdd meeting={activeSession} onAdd={addFollowUp} existingCount={data.followUps.filter((item) => item.meetingId === activeSession.id && !item.done).length} />
                </section>
              </aside>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MeetingFollowUpQuickAdd({ meeting, onAdd, existingCount }) {
  const [task, setTask] = useState('');
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Medium');

  const submit = () => {
    if (!task.trim() || existingCount >= 3) return;
    onAdd({ task, owner, dueDate, priority, meetingId: meeting.id });
    setTask('');
    setOwner('');
    setDueDate('');
    setPriority('Medium');
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 text-xs text-gray-400">{existingCount}/3 priority actions agreed</div>
      {existingCount >= 3 ? (
        <p className="rounded-xl border border-emerald-400/20 bg-emerald-400/5 p-3 text-xs leading-5 text-emerald-200">Three priorities are already set. Complete, replace or remove one before adding another.</p>
      ) : <>
      <textarea value={task} onChange={(event) => setTask(event.target.value)} rows={3} className="w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="What needs to happen after this meeting?" />
      <input value={owner} onChange={(event) => setOwner(event.target.value)} className="w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" placeholder="Owner" />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} className="w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400" />
        <select value={priority} onChange={(event) => setPriority(event.target.value)} className="w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-sm outline-none focus:border-pink-400">
          <option>Low</option><option>Medium</option><option>High</option><option>Urgent</option>
        </select>
      </div>
      <button type="button" onClick={submit} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-pink-500 px-4 py-2.5 text-sm font-semibold hover:bg-pink-400"><Plus size={16} /> Add follow-up</button>
      </>}
    </div>
  );
}

export default MeetingWorkspace;
